import { useInfiniteQuery } from "@tanstack/react-query";
import { useTypeWire } from "@typewirets/react";
import { useDebounce } from "@/hooks/use-debounce";
import { DeepResearchReportsAPIWire } from "@/lib/api/deep-research-reports.api.wire";
import type { ListAIFieldsResponse } from "@/types/deep-research-reports";

/**
 * Query parameters for AI fields API
 */
export interface AIFieldsQueryParams {
  search?: string;
  sort_by?: string;
  sort_direction?: "asc" | "desc";
  filters?: Record<string, unknown> | string;
  limit?: number;
  offset?: number;
}

/**
 * Hook for fetching AI fields with infinite scrolling
 */
export function useInfiniteAIFields(params?: AIFieldsQueryParams) {
  const api = useTypeWire(DeepResearchReportsAPIWire);
  const {
    limit = 50,
    sort_by = "created_at",
    sort_direction = "desc",
    search = "",
    filters,
  } = params || {};

  const debouncedSearch = useDebounce(search, 300);

  return useInfiniteQuery<ListAIFieldsResponse, Error>({
    queryKey: [
      "deep-research/ai-fields/infinite",
      {
        sort_by,
        sort_direction,
        search: debouncedSearch,
        filters,
      },
    ],
    queryFn: async ({ pageParam }) => {
      const offset = typeof pageParam === "number" ? pageParam * limit : 0;

      // Build query parameters
      const queryParams: Record<string, unknown> = {
        limit,
        offset,
        sort_by,
        sort_direction,
        search: debouncedSearch || undefined,
        filters,
      };

      // Call the API with the filters object
      const response = await api.listAllAIFields(queryParams);

      // Return the API response with proper types
      return {
        ai_fields: response.ai_fields,
        total_count: response.total_count || 0,
        limit: response.limit || String(limit),
        offset: response.offset || String(offset),
      };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const currentOffset = parseInt(lastPage.offset || "0", 10);
      const currentLimit = parseInt(lastPage.limit || String(limit), 10);
      const totalCount = lastPage.total_count || 0;

      // Calculate next offset
      const nextOffset = currentOffset + currentLimit;

      // Check if there are more items to fetch
      if (nextOffset < totalCount) {
        return allPages.length; // Return the next page number
      }

      return undefined; // No more pages
    },
  });
}
